// frontend/admin-portal/src/components/users/UserModal.jsx
import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Input,
  Alert,
  Checkbox,
  Badge,
} from "@fiberhome/shared-ui";
import { userService } from "../../services/userService";

const EMPTY = {
  username: "",
  email: "",
  fullName: "",
  phone: "",
  roleId: "",
  password: "",
  isActive: true,
};

export default function UserModal({ open, user, roles, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [generateTemp, setGenerateTemp] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tempPassword, setTempPassword] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setTempPassword("");
    setGenerateTemp(true);
    if (user) {
      setForm({
        username: user.username || "",
        email: user.email || "",
        fullName: user.fullName || "",
        phone: user.phone || "",
        roleId: user.roleId ?? user.role?.id ?? "",
        password: "",
        isActive: user.isActive !== false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        roleId: Number(form.roleId),
      };
      if (!user?.id && generateTemp) delete payload.password;
      if (user?.id) delete payload.password; // password changes go through reset flow
      const result = user?.id
        ? await userService.update(user.id, payload)
        : await userService.create(payload);

      if (!user?.id && result.temporaryPassword) {
        setTempPassword(result.temporaryPassword);
        onSaved?.(); // refresh list
        return; // keep modal open to show the temp password
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!user?.id;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? `Edit: ${user?.fullName}` : "New User"}
      footer={
        tempPassword ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={saving}>
              {isEditing ? "Save Changes" : "Create User"}
            </Button>
          </>
        )
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {tempPassword && (
        <div className="fh-temp-password">
          <Alert variant="success" title="User created">
            Share this temporary password with the user. They will be asked to
            change it on first login.
          </Alert>
          <div className="fh-temp-password__box">
            <code>{tempPassword}</code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigator.clipboard.writeText(tempPassword)}
            >
              Copy
            </Button>
          </div>
        </div>
      )}

      {!tempPassword && (
        <>
          <div className="row g-3">
            <div className="col-md-6">
              <Input
                label="Full Name *"
                value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Username *"
                value={form.username}
                onChange={(e) => set("username", e.target.value)}
                disabled={isEditing}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Email *"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="fh-input-label">Role *</label>
              <select
                className="fh-input"
                value={form.roleId}
                onChange={(e) => set("roleId", e.target.value)}
              >
                <option value="">Select a role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.displayName}
                  </option>
                ))}
              </select>
            </div>

            {!isEditing && (
              <div className="col-md-6 d-flex flex-column justify-content-end">
                <label className="fh-checkbox">
                  <input
                    type="checkbox"
                    checked={generateTemp}
                    onChange={(e) => setGenerateTemp(e.target.checked)}
                  />
                  <span>Auto-generate a temporary password</span>
                </label>
                {!generateTemp && (
                  <Input
                    type="password"
                    label="Password"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    hint="Min 8 chars, one uppercase, one lowercase, one digit"
                  />
                )}
              </div>
            )}

            <div className="col-12">
              <label className="fh-checkbox">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                />
                <span>Account is active</span>
              </label>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
